import { memo, useCallback, useState } from 'react';
import Avatar from '../Avatar.jsx';
import Badge from '../Badge.jsx';
import Button from '../Button.jsx';
import Checkbox from '../Checkbox.jsx';
import Dropdown from '../Dropdown.jsx';
import IconHighlight from '../IconHighlight.jsx';
import {
  entityListAiInsightClass,
  entityListAiInsightDotClass,
  entityListAiInsightTextClass,
  entityListAiInsightTitleClass,
  entityListContentClass,
  entityListCtaDividerClass,
  entityListCtaGroupClass,
  entityListDescriptionClass,
  entityListDescriptionRowClass,
  entityListFooterRowClass,
  entityListHeaderLeftClass,
  entityListHeaderRightClass,
  entityListHeaderRowClass,
  entityListRootClass,
  entityListSecondaryMetaClass,
  entityListTimestampClass,
  entityListTitleClass,
} from './entityListClasses.js';
import { EntityListMetaItems, EntityListSecondaryMetaItems } from './entityListUtils.jsx';
import { EntityListTags } from './EntityListTags.jsx';
import { ChevronDown, MoreVertical, Sparkles } from 'lucide-react';

const EMPTY_ARRAY = [];

/**
 * Entity List row (Figma DS AIMS OS — node 11815:26388).
 * High-density list item for conversations, tasks, tickets, and agent activities.
 * Wrap with {@link CartContainer} in list views.
 */
const EntityList = memo(({
  title,
  checkboxChecked = false,
  onCheckboxChange,
  icon,
  iconHighlight,
  avatar,
  primaryMeta = EMPTY_ARRAY,
  pinned,
  primaryCta,
  secondaryCta,
  tertiaryCta,
  status,
  timestamp,
  dropdown,
  description,
  descriptionMaxLength = 220,
  aiInsight,
  aiInsightExpandThreshold = 100,
  aiInsightViewMoreThreshold = 600,
  aiInsightViewMoreLabel = 'View more',
  aiInsightExpandAriaLabel = 'Expand AI insight',
  aiInsightCollapseAriaLabel = 'Collapse AI insight',
  secondaryMeta = EMPTY_ARRAY,
  tags = EMPTY_ARRAY,
  className = '',
  ...props
}) => {
  const [descriptionExpanded, setDescriptionExpanded] = useState(false);
  const [aiInsightExpanded, setAiInsightExpanded] = useState(false);

  const stop = useCallback((e) => e.stopPropagation(), []);

  const toggleDescription = useCallback((e) => {
    e.stopPropagation();
    setDescriptionExpanded((value) => !value);
  }, []);

  const toggleAiInsight = useCallback((e) => {
    e.stopPropagation();
    setAiInsightExpanded((v) => !v);
  }, []);

  const handleViewMore = useCallback((e) => {
    e.stopPropagation();
    aiInsight?.onViewMore?.();
  }, [aiInsight?.onViewMore]);

  const resolvedDescription =
    typeof description === 'string' && description.length > descriptionMaxLength && !descriptionExpanded
      ? `${description.slice(0, descriptionMaxLength).trim()}…`
      : description;

  const showDescriptionToggle =
    typeof description === 'string' && description.length > descriptionMaxLength;

  const aiInsightText = aiInsight?.text ?? '';
  const showAiInsightExpand = aiInsightText.length > aiInsightExpandThreshold;
  const showViewMore =
    showAiInsightExpand &&
    aiInsightText.length > aiInsightViewMoreThreshold &&
    Boolean(aiInsight?.onViewMore);
  const displayedAiText =
    showViewMore && aiInsightExpanded
      ? `${aiInsightText.slice(0, aiInsightViewMoreThreshold).trimEnd()}…`
      : aiInsightText;

  const hasDropdown = Boolean(dropdown?.items?.length);
  const MenuIcon = dropdown?.props?.triggerIcon ?? MoreVertical;
  const hasPrimaryCta = Boolean(primaryCta);
  const hasSecondaryCta = Boolean(secondaryCta);
  const hasTertiaryCta = Boolean(tertiaryCta);
  const hasCtaGroup = hasPrimaryCta || hasSecondaryCta || hasTertiaryCta;

  return (
    <div className={[entityListRootClass, className].filter(Boolean).join(' ')} {...props}>
      <div className={entityListContentClass}>
        <div className={entityListHeaderRowClass}>
          <div className={entityListHeaderLeftClass}>
            {onCheckboxChange ? (
              <span onClick={stop}>
                <Checkbox
                  size="small"
                  checked={checkboxChecked}
                  onChange={onCheckboxChange}
                  aria-label={checkboxChecked ? 'Deselect item' : 'Select item'}
                />
              </span>
            ) : null}

            {icon ? (
              <IconHighlight
                icon={icon}
                size="small"
                bgColor="Alert"
                iconColor="Alert"
                {...iconHighlight}
              />
            ) : null}

            {avatar != null ? (
              <Avatar
                size="md"
                src={avatar.src}
                initials={avatar.initials}
                alt={avatar.alt ?? title ?? 'Avatar'}
                className={[
                  'h-6 w-6 min-h-6 min-w-6 border-[#80afff] bg-[#2173ff] text-[10px] font-semibold text-white',
                  avatar.className,
                ]
                  .filter(Boolean)
                  .join(' ')}
              />
            ) : null}

            {title != null && title !== '' ? (
              <span className={entityListTitleClass}>{title}</span>
            ) : null}

            {primaryMeta.length > 0 ? (
              <EntityListMetaItems items={primaryMeta} />
            ) : null}

            {pinned?.label ? (
              <Badge variant={pinned.variant ?? 'error'} size="small" icon={pinned.icon}>
                {pinned.label}
              </Badge>
            ) : null}
          </div>

          <div className={entityListHeaderRightClass} onClick={stop}>
            {hasCtaGroup ? (
              <div className={entityListCtaGroupClass}>
                {hasPrimaryCta ? (
                  <Button
                    size="sm"
                    leftIcon={primaryCta.icon}
                    variant={primaryCta.variant ?? 'classicPrimary'}
                    onClick={primaryCta.onClick}
                    disabled={primaryCta.disabled}
                    className="h-[27px] px-3 py-1"
                  >
                    {primaryCta.label}
                  </Button>
                ) : null}
                {hasSecondaryCta ? (
                  <Button
                    size="sm"
                    leftIcon={secondaryCta.icon}
                    variant={secondaryCta.variant ?? 'secondary'}
                    onClick={secondaryCta.onClick}
                    disabled={secondaryCta.disabled}
                    className="h-[27px] px-3 py-1"
                  >
                    {secondaryCta.label}
                  </Button>
                ) : null}
                {hasTertiaryCta ? (
                  <Button
                    size="sm"
                    leftIcon={tertiaryCta.icon}
                    variant={tertiaryCta.variant ?? 'tertiary'}
                    onClick={tertiaryCta.onClick}
                    disabled={tertiaryCta.disabled}
                    className="h-7 px-3 py-1"
                  >
                    {tertiaryCta.label}
                  </Button>
                ) : null}
                <span className={entityListCtaDividerClass} aria-hidden />
              </div>
            ) : null}

            {status?.label ? (
              <Badge variant={status.variant ?? 'success'} size="small">
                {status.label}
              </Badge>
            ) : null}

            {timestamp != null && timestamp !== '' ? (
              <span className={entityListTimestampClass}>{timestamp}</span>
            ) : null}

            {hasDropdown ? (
              <Dropdown
                {...dropdown.props}
                align={dropdown.props?.align ?? 'right'}
                trigger={
                  <Button
                    size="sm"
                    variant="ghost"
                    leftIcon={MenuIcon}
                    aria-label={dropdown.props?.triggerAriaLabel ?? 'Open menu'}
                    customIconSize={16}
                    className="!size-7"
                  />
                }
              >
                {dropdown.items.map((item, index) =>
                  item.separator ? (
                    <Dropdown.Separator key={`separator-${index}`} />
                  ) : (
                    <Dropdown.Item
                      key={item.id ?? item.key ?? `${item.label}-${index}`}
                      icon={item.icon}
                      variant={item.variant}
                      disabled={item.disabled}
                      onClick={item.onClick}
                    >
                      {item.label}
                    </Dropdown.Item>
                  ),
                )}
              </Dropdown>
            ) : null}
          </div>
        </div>

        {description != null && description !== '' ? (
          <div className={entityListDescriptionRowClass}>
            <p className={entityListDescriptionClass}>{resolvedDescription}</p>
            {showDescriptionToggle ? (
              <Button
                size="sm"
                variant="ghost"
                leftIcon={ChevronDown}
                onClick={toggleDescription}
                aria-expanded={descriptionExpanded}
                aria-label={descriptionExpanded ? 'Collapse description' : 'Expand description'}
                customIconSize={16}
                className={`!size-7 shrink-0 transition-transform${descriptionExpanded ? ' rotate-180' : ''}`}
              />
            ) : null}
          </div>
        ) : null}

        {aiInsight?.text ? (
          <div
            className={[
              entityListAiInsightClass,
              showAiInsightExpand ? 'w-full items-start' : 'items-center',
            ].join(' ')}
          >
            <Sparkles
              size={16}
              className={`shrink-0 text-purple-700 dark:text-purple-300${showAiInsightExpand ? ' mt-0.5' : ''}`}
              aria-hidden
            />
            <div
              className={[
                'min-w-0 flex-1',
                showAiInsightExpand && !aiInsightExpanded ? 'line-clamp-2' : '',
              ].filter(Boolean).join(' ')}
            >
              <span className={entityListAiInsightTitleClass}>{aiInsight.label ?? 'AI Action'}</span>
              <span className={entityListAiInsightDotClass} aria-hidden> · </span>
              <span className={entityListAiInsightTextClass}>{displayedAiText}</span>
            </div>
            {showAiInsightExpand ? (
              <div className="flex shrink-0 items-center gap-1 self-start">
                {showViewMore ? (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={handleViewMore}
                    className="h-7 px-2 text-xs"
                  >
                    {aiInsightViewMoreLabel}
                  </Button>
                ) : null}
                <Button
                  size="sm"
                  variant="ghost"
                  leftIcon={ChevronDown}
                  onClick={toggleAiInsight}
                  aria-expanded={aiInsightExpanded}
                  aria-label={aiInsightExpanded ? aiInsightCollapseAriaLabel : aiInsightExpandAriaLabel}
                  customIconSize={16}
                  className={`!size-7 transition-transform${aiInsightExpanded ? ' rotate-180' : ''}`}
                />
              </div>
            ) : null}
          </div>
        ) : null}

        {secondaryMeta.length > 0 || tags.length > 0 ? (
          <div className={entityListFooterRowClass}>
            {secondaryMeta.length > 0 ? (
              <EntityListSecondaryMetaItems
                items={secondaryMeta}
                className={entityListSecondaryMetaClass}
              />
            ) : (
              <span className="min-w-0 flex-1" />
            )}

            {tags.length > 0 ? <EntityListTags tags={tags} /> : null}
          </div>
        ) : null}
      </div>
    </div>
  );
});

export default EntityList;
